import { AutotractorPropsType, Domain } from "./layers/types";
import {
  CenterPivotData,
  CenterPivotLeg as CenterPivotLegType,
  Zone,
} from "protos/farm/farm";
import { DateTime } from "luxon";
import {
  DEFAULT_EXCLUDE_FIX_TYPES,
  destinationAlongLine,
} from "common/utils/geo";
import { Feature, FeatureCollection, Point as PointType } from "geojson";
import { Layer, Marker, Source } from "react-map-gl";
import { MarkerTooltip } from "./AccuracyMarker";
import { Point } from "protos/geo/geo";
import { PointFeatureProperties } from "common/utils/farm";
import { rtcLocatorApi } from "common/state/rtcLocatorApi";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import React, {
  FC,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";

const STALE_DEVICE_TIMEOUT_MIN = 5;
const DEVICE_UPDATE_INTERVAL_MIN = 5 / 60;
interface CenterPivotProps {
  zone: Zone;
}
interface CenterPivotLegProps {
  centerPivot: CenterPivotLegType & Pick<CenterPivotData, "center">;
}
export const CenterPivot: FC<CenterPivotProps> = ({ zone }) => {
  const legsWithCenters = useMemo(() => {
    const pivot = zone.contents?.centerPivot;
    if (pivot) {
      return pivot.legs.map((l) => ({ ...l, center: pivot.center }));
    }
    return [];
  }, [zone]);
  return (
    <>
      {legsWithCenters.map((l, i) => (
        <CenterPivotLeg key={`${i}-${l.endpointDeviceId}`} centerPivot={l} />
      ))}
    </>
  );
};
const CenterPivotLeg: FunctionComponent<CenterPivotLegProps> = ({
  centerPivot,
}) => {
  const { center, endpointDeviceId, lengthMeters } = centerPivot;
  const { data: latestEndpointLocation } =
    rtcLocatorApi.endpoints.getDeviceLocationHistory.useQuery(
      {
        deviceId: endpointDeviceId,
        pageSize: 1,
        desc: true,
        excludeFixTypes: DEFAULT_EXCLUDE_FIX_TYPES,
      },
      { pollingInterval: DEVICE_UPDATE_INTERVAL_MIN * 60 * 1000 }
    );

  const endPoint = latestEndpointLocation?.history?.records[0];
  const [isDeviceStale, setIsDeviceStale] = useState(false);

  useEffect(() => {
    const lastUpdated = endPoint?.timestamp;
    if (lastUpdated) {
      const recordStaleness = (): void => {
        const isStale =
          DateTime.now().diff(DateTime.fromISO(lastUpdated), "minutes")
            .minutes > STALE_DEVICE_TIMEOUT_MIN;
        setIsDeviceStale(isStale);
      };
      // run once on initialization
      recordStaleness();

      // Check to see if the device is stale condition every minute
      const intervalId = setInterval(() => {
        recordStaleness();
      }, 60 * 1000);

      return () => clearInterval(intervalId);
    }
  }, [endPoint?.timestamp]);

  const lineGeoJson = useMemo(
    () =>
      center && endPoint?.point
        ? createLine(center, endPoint.point, lengthMeters)
        : undefined,
    [center, endPoint?.point, lengthMeters]
  );

  if (!center) {
    return;
  }

  const staleColor = theme.colors.carbon.gray.dark;

  const centerPointData: Feature<PointType, PointFeatureProperties> = {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [center.lng, center.lat],
    },
    properties: {
      type: AutotractorPropsType.POINT,
      pointId: center.id?.id,
      captureInfo: center.captureInfo,
      domain: Domain.AUTOTRACTOR,
      __portalFeatureProps: true,
    },
  };
  return (
    <>
      <Source type="geojson" data={centerPointData}>
        <Layer
          type="circle"
          paint={{
            "circle-color": theme.colors.carbon.map.farms.centerPivot,
            "circle-stroke-color": theme.colors.carbon.gray.dark,
            "circle-stroke-width": 1,
          }}
        />
      </Source>
      {endPoint?.point && (
        <>
          {/* the end point */}
          <Marker longitude={endPoint.point.lng} latitude={endPoint.point.lat}>
            <MarkerTooltip id={endPoint.deviceId}>
              <CircleIcon
                className="block"
                fillColor={
                  isDeviceStale
                    ? staleColor
                    : theme.colors.carbon.map.farms.centerPivot
                }
              />
            </MarkerTooltip>
          </Marker>
          {/* the connecting line */}
          {lineGeoJson && (
            <Source
              id={`pivot-irrigation-${center.id?.id}-endpoint-source`}
              type="geojson"
              data={lineGeoJson}
            >
              <Layer
                id={`pivot-irrigation-${center.id?.id}-${endpointDeviceId}-layer`}
                type="line"
                paint={{
                  "line-color": isDeviceStale ? staleColor : theme.colors.black,
                  "line-width": 4,
                  ...(isDeviceStale ? { "line-dasharray": [1, 1] } : {}),
                }}
              />
            </Source>
          )}
        </>
      )}
    </>
  );
};

interface CircleIconProps {
  className?: string;
  fillColor: string;
}
const CircleIcon: FunctionComponent<CircleIconProps> = ({
  className,
  fillColor,
}) => (
  <svg viewBox="0 0 12 12" width={14} height={14} className={className}>
    <g fill="none">
      <circle
        cx={6}
        cy={6}
        r={6}
        fill={fillColor}
        stroke="black"
        strokeWidth={2}
      />
    </g>
  </svg>
);

const createLine = (
  pointA: Point,
  pointB: Point,
  lengthM?: number
): FeatureCollection => {
  const posA = [pointA.lng, pointA.lat];
  const posB = [pointB.lng, pointB.lat];
  const coordinates = lengthM
    ? destinationAlongLine(posA, posB, lengthM)
    : [posA, posB];
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates,
        },
        properties: {},
      },
    ],
  };
};
